import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@shared/contexts/AuthContext";
import { Button } from "@shared/components/ui/button";
import { useExhibitions } from "@shared/api/hooks/useExhibitions";
import type { ExhibitionState } from "@shared/api/exhibitions";
import type { ListStatus } from "@shared/api/listStatus";
import {
  Plus,
  ImageIcon,
  LogOut,
  Key,
  MapPin,
  BarChart2,
  Users,
  Building2,
  LayoutGrid,
  ClipboardList,
  Settings,
  Gauge,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@shared/components/ui/dropdown-menu";

import StatusFilter from "@staff/components/StatusFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const API = `${BACKEND_URL}/api`;

// F-B — Exhibitions list (admin / editor / analyst).
// Server-side scope filtering: non-admin actors only see exhibitions
// matching their team_ids OR department_id (Model A — OR semantics).
// Staff status filter: a Status dropdown (Active & Archived / Purged / All)
// re-queries via the `status` param and replaces the legacy F-C "Show archived"
// checkbox. Default (Active & Archived) shows non-purged rows; archived + purged
// rows render muted, and clicking one opens the read-only (lockdown) form.

// Ownership column: resolved names, falling back to the raw id when an
// owner no longer resolves (still diagnosable); null = unowned.
function ownershipLabel(ex: {
  ownerTeamId: string | null;
  ownerDepartmentId: string | null;
  ownerTeamName?: string | null;
  ownerDepartmentName?: string | null;
}): string | null {
  const parts = [
    ex.ownerTeamName ?? ex.ownerTeamId,
    ex.ownerDepartmentName ?? ex.ownerDepartmentId,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

function stateLabel(state: ExhibitionState): string {
  if (state === "published") return "▣ published";
  if (state === "archived") return "▼ archived";
  if (state === "purged") return "✕ purged";
  return "◌ draft";
}

const ExhibitionsList = () => {
  const navigate = useNavigate();
  const { username, logout, isAdmin, role } = useAuth();
  const isEditor = role === "editor";
  const [status, setStatus] = useState<ListStatus>("active");
  const { data: rows = [], isLoading } = useExhibitions({ status });

  const handleLogout = () => {
    logout();
    navigate("/staff/login");
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="exhibitions-list-page"
    >
      {/* Header — Exhibitions is the staff home page; the top menu bar
          lives here (moved from the exhibit-management page). */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold">Exhibitions</h1>
              <p className="text-sm text-muted-foreground">
                Welcome, {username}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => navigate("/staff/zones")}
                variant="outline"
                size="sm"
                data-testid="manage-zones-button"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Zones
              </Button>
              <Button
                onClick={() => navigate("/staff/dashboard")}
                variant="outline"
                size="sm"
                data-testid="manage-exhibits-button"
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                Exhibits
              </Button>
              <Button
                onClick={() => navigate("/staff/statistics")}
                variant="outline"
                size="sm"
                data-testid="visitor-statistics-button"
              >
                <BarChart2 className="w-4 h-4 mr-2" />
                Statistics
              </Button>
              {/* Settings groups the admin screens + Password (everyone)
                  + the AI Usage Meter (admin + editor) — so it renders
                  for every role. Item order per 2026-07-28 request:
                  Org, Users, Password, Log, AI Usage Meter. Moved items
                  keep their legacy test ids (e2e stability). */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="settings-menu-button"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isAdmin && (
                    <DropdownMenuItem
                      onClick={() => navigate("/staff/org")}
                      data-testid="manage-org-button"
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      Org
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem
                      onClick={() => navigate("/staff/users")}
                      data-testid="manage-users-button"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Users
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigate("/staff/change-password")}
                    data-testid="change-password-button"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Password
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem
                      onClick={() => navigate("/staff/audit")}
                      data-testid="manage-audit-button"
                    >
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Log
                    </DropdownMenuItem>
                  )}
                  {(isAdmin || isEditor) && (
                    <DropdownMenuItem
                      onClick={() => navigate("/staff/ai-usage")}
                      data-testid="ai-usage-menu-item"
                    >
                      <Gauge className="w-4 h-4 mr-2" />
                      AI Usage Meter
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <StatusFilter value={status} onChange={setStatus} />
          <Button
            onClick={() => navigate("/staff/exhibition/create")}
            data-testid="create-exhibition-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create exhibition
          </Button>
        </div>
        {isLoading ? (
          <div className="text-center py-12" data-testid="loading-state">
            Loading...
          </div>
        ) : rows.length === 0 ? (
          <div
            className="text-center py-12 border border-border rounded-lg"
            data-testid="empty-state"
          >
            <p className="text-muted-foreground">
              No exhibitions visible — click Create exhibition to add one, or
              ask an admin to share an existing one with your team.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <table className="w-full" data-testid="exhibitions-table">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Code
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Image
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Title (TC)
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Ownership
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Zones
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    State
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((ex) => (
                  <tr
                    key={ex.id}
                    onClick={() => navigate(`/staff/exhibition/${ex.id}/edit`)}
                    className={`cursor-pointer hover:bg-muted/30 ${
                      ex.state === "archived" || ex.state === "purged"
                        ? "bg-muted/40 text-muted-foreground"
                        : ""
                    }`}
                    data-testid={`exhibition-row-${ex.id}`}
                  >
                    <td className="px-6 py-3 text-sm font-mono">{ex.code}</td>
                    <td className="px-6 py-3">
                      {ex.coverImageId ? (
                        <img
                          src={`${API}/assets/${ex.coverImageId}`}
                          alt={ex.titleTc}
                          className="w-12 h-12 object-cover rounded"
                          data-testid={`exhibition-image-${ex.id}`}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const ph = e.currentTarget.nextElementSibling;
                            if (ph instanceof HTMLElement)
                              ph.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className="w-12 h-12 bg-muted rounded items-center justify-center"
                        style={{
                          display: ex.coverImageId ? "none" : "flex",
                        }}
                      >
                        <ImageIcon className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm">{ex.titleTc}</td>
                    <td
                      className="px-6 py-3 text-sm"
                      data-testid={`exhibition-ownership-${ex.id}`}
                    >
                      {ownershipLabel(ex) ?? (
                        <span className="text-muted-foreground italic">
                          Unowned
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm">{ex.zoneCount}</td>
                    <td
                      className="px-6 py-3 text-sm"
                      data-testid={`exhibition-state-${ex.id}`}
                    >
                      {stateLabel(ex.state)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExhibitionsList;
